<template>
	<div v-if="isMenuOpened" class="dimmed" @click="isMenuOpened = false"></div>
	<div class="tabBar_container sticky_tab">
		<div class="tabBox_wrap">
			<div ref="tabBox" class="tab_box">
				<button
					v-for="(item, index) in props.items"
					:key="index"
					:ref="(el) => setItemRef(el, index)"
					:value="item"
					class="tab_item"
					:class="(category === item || (!category && index === 0)) && 'selected'"
					@click="onClickTab(item, $event)"
				>
					{{ item }}
				</button>
			</div>
			<div class="btn_box">
				<button class="tab_icon">
					<img
						src="@/assets/img/common/arrow_down.svg"
						alt="탭메뉴"
						loading="lazy"
						@click="isMenuOpened = !isMenuOpened"
					/>
				</button>
				<div v-if="isMenuOpened" class="tab_allItem">
					<div class="allItem_title">전체보기</div>
					<div class="allItem_content">
						<button
							v-for="(item, index) in items"
							:key="index"
							class="content_menu"
							@click="onClickSubTab(item, index)"
						>
							{{ item }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ComponentPublicInstance, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

// -----variable-----
const props = defineProps<{
	items: string[] | undefined;
	selectedScrollTab?: string;
}>();

const emit = defineEmits(['update:category']);

const route = useRoute();
const tabBox = ref<HTMLDivElement | null>(null);
const isMenuOpened = ref(false);
const refsArray = ref<HTMLElement[]>([]);
const category = ref<string>('');

// -----function-----
// HTML 요소를 중앙에 위치시키는 함수
const scrollToCenter = (element: HTMLElement) => {
	if (tabBox.value) {
		const container = tabBox.value;
		const containerWidth = container.clientWidth;
		const elementWidth = element.clientWidth;
		const elementLeft = element.offsetLeft;
		const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;
		container.scrollTo({
			left: scrollPosition,
			behavior: 'smooth',
		});
	}
};
// 탭 클릭 시 실행 함수
const onClickTab = (tab: string, event: Event) => {
	const target = event.target as HTMLElement;
	category.value = tab;
	scrollToCenter(target);
	emit('update:category', tab);
};
// 서브 탭 클릭 시 실행 함수
const onClickSubTab = (tab: string, index: number) => {
	const target = refsArray.value[index];
	if (target) {
		scrollToCenter(target);
	}
	category.value = tab;
	isMenuOpened.value = false;
	emit('update:category', tab);
};
// 동적으로 Ref 부여하는 함수
const setItemRef = (el: Element | ComponentPublicInstance | null, index: number) => {
	if (el instanceof HTMLButtonElement) refsArray.value[index] = el;
};

// -----composition-----
onMounted(() => {
	const initialTab = route.query.tab || props.items?.[0];
	const initialTabElement = tabBox.value?.querySelector<HTMLElement>(`.tab_item[value="${initialTab}"]`);
	if (initialTabElement) {
		scrollToCenter(initialTabElement);
	}
});

watch(
	() => props.selectedScrollTab,
	() => {
		const newTabElement = tabBox.value?.querySelector<HTMLElement>(`.tab_item[value="${props.selectedScrollTab}"]`);
		if (newTabElement) {
			category.value = props.selectedScrollTab ?? '';
			scrollToCenter(newTabElement);
		}
	}
);
</script>

<style lang="scss" scoped>
.tabBar_container {
	position: sticky;
	top: -0.1rem;
	background-color: var(--color-bg);
	z-index: 89;
}

.tabBox_wrap {
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	border-bottom: 1px solid #393d45;
	@media screen and (max-width: 1280px) {
		padding: 0 2rem;
	}
	.tab_icon {
		font-size: var(--font-s-regular);
		font-weight: var(--font-w-medium);
		line-height: 1.7;
		color: var(--color-font-white);
		white-space: nowrap;
		img {
			width: 2.4rem;
			height: 2.4rem;
		}
	}
}
.btn_box {
	display: flex;
}
.tab_box {
	display: flex;
	flex-direction: row;
	max-width: 68rem;
	overflow: scroll;
	.tab_item {
		font-size: var(--font-s-regular);
		font-weight: var(--font-w-medium);
		line-height: 1.7;
		color: var(--color-font-white);
		display: flex;
		flex: 1;
		align-items: center;
		justify-content: center;
		height: 5.2rem;
		white-space: nowrap;
		padding: 1.6rem;
	}
	.selected {
		color: var(--color-primary);
		border-bottom: 1px solid var(--color-primary);
	}
}

.tab_box::-webkit-scrollbar {
	display: none;
}

.tab_allItem {
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	position: absolute;
	top: 103%;
	right: 0;
	z-index: 89;
	background-color: var(--color-bg-mid);
	padding: 2.4rem;
	border-radius: 0.8rem;
	box-shadow: 4px 4px 10px 0px #0000004d;
	@media screen and (max-width: 1280px) {
		margin: 0 2rem;
	}

	.allItem_title {
		font-size: var(--font-s-large);
		font-weight: var(--font-w-semi-bold);
	}
	.allItem_content {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		.content_menu {
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-medium);
			line-height: 1.79rem;
			white-space: nowrap;
			padding: 1.3rem 1.6rem;
			border: 1px solid var(--color-base-mid);
			border-radius: 6px;
		}
	}
}
.dimmed {
	position: fixed;
	background-color: #171616bf;
	top: 0px;
	left: 0px;
	height: 100%;
	width: 100%;
	z-index: 80;
}
</style>
